'use client';

import React from 'react';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import styled from 'styled-components';
import { useHasTripAdvisorHotels } from '@/app/common/reviews/useHasTripAdvisorHotels.ts';
import { toTADisplayScore } from '@/app/components/hotel-detail/trip-advisor/tripadvisor.helper.ts';
import { RATING_RAW_THRESHOLD, RATING_VALUE } from '@/app/common/filters/core/ratingValues.ts';
import {
  trustYouExcellent,
  trustYouFair,
  trustYouGood,
  trustYouPoor,
  trustYouVeryGood,
} from '@/app/components/search/styles/colors.ts';

type Props = {
  value: RATING_VALUE;
  translationKey: string;
};

// TODO: deduplicate
export const RatingStyled = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  div {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 44px;
    border-radius: 4px;
    margin-right: 8px;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    text-align: center;
    color: white;
  }

  &.filter-rating-label-excellent div {
    background-color: ${trustYouExcellent};
  }

  &.filter-rating-label-verygood div {
    background-color: ${trustYouVeryGood};
  }

  &.filter-rating-label-good div {
    background-color: ${trustYouGood};
  }

  &.filter-rating-label-pleasant div {
    background-color: ${trustYouFair};
  }

  &.filter-rating-label-poor div {
    background-color: ${trustYouPoor};
  }
`;

// TODO: deduplicate
export const getRatingNumber = (rating: RATING_VALUE) => {
  switch (rating) {
    case RATING_VALUE.EXCELLENT:
      return '8.7+';
    case RATING_VALUE.VERY_GOOD:
      return '8.1+';
    case RATING_VALUE.GOOD:
      return '7.5+';
    case RATING_VALUE.PLEASANT:
      return '6.0+';
    default:
      return '≤6.0';
  }
};

// Derived via toTADisplayScore so the displayed value matches the score shown everywhere else.
export const getTARatingNumber = (rating: string) => {
  const threshold = RATING_RAW_THRESHOLD[rating];
  return threshold !== undefined ? `${toTADisplayScore(threshold)}+` : '';
};

// TA filter rows drop the coloured band and bubbles: just the bold score threshold + label.
const TARatingStyled = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const TAThreshold = styled.span`
  font-weight: 700;
`;

const RatingLabel = ({ value, translationKey }: Props) => {
  const { t } = useTranslation();
  // Filters are page-level (no per-hotel scale): TripAdvisor thresholds are forced as soon as
  // one hotel in the result list is TripAdvisor, otherwise the TrustYou thresholds show.
  const isTripAdvisor = useHasTripAdvisorHotels();

  if (isTripAdvisor) {
    return (
      <TARatingStyled className={`filter-rating-label-${value}`}>
        <TAThreshold>{getTARatingNumber(value)}</TAThreshold>
        {t(translationKey)}
      </TARatingStyled>
    );
  }

  return (
    <RatingStyled className={`filter-rating-label-${value}`}>
      <div>{getRatingNumber(value)}</div>
      {t(translationKey)}
    </RatingStyled>
  );
};

export default RatingLabel;
